import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID, Signal, computed, signal, input } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { RouterModule } from '@angular/router';
import { MatMenuModule } from '@angular/material/menu';
import { OpeningHoursService, OpeningHoursStatus } from '../../../services/opening-hours.service';
import { ScrollService } from '../../../services/scroll.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { InstallMethod, PwaService } from '../../../services/pwa-service.service';
import { WebsiteStateService } from '../../../state-management/services/website-state.service';
import { Page, Result } from '../../../state-management/model/interfaces/state.model';
import { EditableImageComponent } from '../../base/image/editable-image/editable-image.component';
import { FileType } from '../../../state-management/model/enums/state.enums';
import { ImageCoordinates } from '../../../state-management/model/interfaces/screen-size.model';
import { CoordinatesSystem, ImageFormat, NoBreakpoints, ScreenWidthBreakpoints } from '../../../state-management/model/enums/screen-size.enums';
import { ScreensizeStateService } from '../../../state-management/services/screensize-state.service';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { AnalyticsService } from '../../../services/analytics.service';


@Component({
  standalone: true,
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  imports: [
    MatToolbarModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatIconModule,
    CommonModule,
    MatMenuModule,
    RouterModule,
    EditableImageComponent,
    MatDialogModule
  ]
})


export class Navbar implements OnInit, OnDestroy {
  readonly componentId = input.required<string>();

  #subscriptions: any[] = [];

  protected status = signal(OpeningHoursStatus.Undefined);
  protected statusText = computed(() => {
    if (this.status() === OpeningHoursStatus.Undefined) return '';
    return this.status() === OpeningHoursStatus.Closed ? '🔒 Wir haben geschlossen!' : this.status() === OpeningHoursStatus.ClosingSoon ? '⏳ Wir schließen bald!' : '🌻 Wir haben geöffnet!'
  });

  public supportedFileTypes = [FileType.WEBP, FileType.PNG];
  public imageReference: ImageCoordinates = {
    coordinatesSystem: CoordinatesSystem.NoCoordinates,
    formatMap: {
      [NoBreakpoints.Unset]: ImageFormat.Free,
    }
  };
  public logoId!: Signal<Result<string, string>>;
  public closeBanner: boolean = false;
  public isAppInstalled: boolean = false;
  public isUsingBrowser: boolean = true;
  public installMethod: InstallMethod | null = null;

  public isSmallScreen$: Signal<boolean> = this.screensizeState.isScreenSizes(CoordinatesSystem.ScreenWidth, [ScreenWidthBreakpoints.Small, ScreenWidthBreakpoints.XSmall]);
  public navbarPages$: Signal<Array<{ group: string | null; pages: Page[] }>> = this.websiteState.navbarPages;
  public storeName$: Signal<string | null> = this.websiteState.storeName;
  public showOpeningBanner$: Signal<boolean> = this.websiteState.showOpeningBanner;

  constructor(
    @Inject(PLATFORM_ID)
    private platformId: Object,
    private pwaService: PwaService,
    private ohs: OpeningHoursService,
    private websiteState: WebsiteStateService,
    private screensizeState: ScreensizeStateService,
    public scrollService: ScrollService,
    private analyticsService: AnalyticsService,
    public dialog: MatDialog
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.#subscriptions.push(
        this.ohs.status$.subscribe((status: OpeningHoursStatus | undefined) => {
          if (status) {
            this.status.set(status);
          }
        })
      );
    }
  }

  ngOnInit(): void {
    this.logoId = this.websiteState.getSingleElementIdByLabelAndComponent(this.componentId(), 'logo');
    this.#subscriptions.push(
      this.pwaService.isAppInstalled$.subscribe(isAppInstalled => {
        this.isAppInstalled = isAppInstalled;
      }),
      this.pwaService.isUsingBrowser$.subscribe(isUsingBrowser => {
        this.isUsingBrowser = isUsingBrowser;
      }),
      this.pwaService.installMethod$.subscribe(method => {
        this.installMethod = method;
      })
    );
  }

  ngOnDestroy(): void {
    this.#subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  public installPWA() {
    this.analyticsService.trackClick('install_pwa_button');
    this.pwaService.installPWA();
  }

  public showManualInstallInstructions() {
    this.analyticsService.trackClick('install_pwa_button');
    this.pwaService.showManualInstallInstructions();
  }

  public showInstallDeniedMessage() {
    this.analyticsService.trackClick('install_pwa_button');
    this.pwaService.showInstallDeniedMessage();
  }

  public contactButton(): void {
    this.scrollService.navigateAndScrollTo('/', 'contact');
  }

  public aboutusButton(): void {
    this.scrollService.navigateAndScrollTo('/', 'about-us');
  }
}